import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { StandingsIntegrityPanel } from "@/components/StandingsIntegrityPanel";
import { 
  Users, 
  Trophy, 
  Calendar, 
  Newspaper, 
  Image, 
  MessageCircle,
  TrendingUp,
  UserCircle
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import MigrationsPanel from "@/pages/admin/MigrationsPanel";
import { useAuth } from "@/App";
import { canAccess } from "@/lib/staffAccess";

const QUICK_ACTIONS = [
  { label: "+ Add Match", href: "/admin/matches", area: "matches", testid: "add-match", cls: "bg-[#FFC72C] text-black hover:bg-[#E6B325]" },
  { label: "+ Add Team", href: "/admin/teams", area: "data", testid: "add-team", cls: "bg-[#09090b] text-white hover:bg-gray-800" },
  { label: "+ Add News", href: "/admin/news", area: "news", testid: "add-news", cls: "bg-[#009E60] text-white hover:bg-[#008050]" },
  { label: "+ Add Player", href: "/admin/players", area: "data", testid: "add-player", cls: "bg-gray-600 text-white hover:bg-gray-700" },
];

const AdminDashboard = () => {
  const { user } = useAuth();
  const isFullAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, matchesRes, teamsRes] = await Promise.all([
          axios.get(`${API}/stats/dashboard`, { withCredentials: true }),
          axios.get(`${API}/matches`),
          axios.get(`${API}/teams`)
        ]);

        setStats(statsRes.data);
        setRecentMatches(matchesRes.data.slice(0, 5));
        
        const tMap = {};
        teamsRes.data.forEach(t => { tMap[t.team_id] = t; });
        setTeams(tMap);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    { label: "Teams", value: stats?.teams || 0, icon: Users, color: "bg-blue-500", href: "/admin/teams", area: "data" },
    { label: "Players", value: stats?.players || 0, icon: UserCircle, color: "bg-green-500", href: "/admin/players", area: "data" },
    { label: "Competitions", value: stats?.competitions || 0, icon: Trophy, color: "bg-[#FFC72C]", href: "/admin/competitions", area: "data" },
    { label: "Matches", value: stats?.matches || 0, icon: Calendar, color: "bg-purple-500", href: "/admin/matches", area: "matches" },
    { label: "News Articles", value: stats?.news || 0, icon: Newspaper, color: "bg-orange-500", href: "/admin/news", area: "news" },
    { label: "Media Items", value: stats?.media || 0, icon: Image, color: "bg-pink-500", href: "/admin/media", area: "news" },
  ].filter((c) => canAccess(user, c.area));

  return (
    <AdminLayout title="Dashboard">
      <div data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))
          ) : (
            statCards.map((stat, i) => (
              <Link
                key={i}
                to={stat.href}
                className="bg-white p-6 border border-gray-200 hover:shadow-lg transition-shadow"
                data-testid={`stat-card-${stat.label.toLowerCase()}`}
              >
                <div className={`w-12 h-12 ${stat.color} flex items-center justify-center mb-4`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-heading font-bold text-[#09090b]">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </Link>
            ))
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Quick Actions */}
          <div className="bg-white p-6 border border-gray-200">
            <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] mb-6">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-4">
              {QUICK_ACTIONS.filter((q) => canAccess(user, q.area)).map((q) => (
                <Link key={q.testid} to={q.href} className={`p-4 font-bold text-center transition-colors ${q.cls}`} data-testid={`quick-action-${q.testid}`}>
                  {q.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Unread Messages */}
          {isFullAdmin && <div className="bg-white p-6 border border-gray-200">
            <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] mb-6">
              <MessageCircle className="inline-block w-5 h-5 mr-2 text-[#FFC72C]" />
              Contact Messages
            </h3>
            <div className="flex items-center justify-between p-4 bg-gray-50">
              <div>
                <p className="text-3xl font-heading font-bold text-[#09090b]">{stats?.unread_messages || 0}</p>
                <p className="text-sm text-gray-500">Unread Messages</p>
              </div>
              <TrendingUp className="w-8 h-8 text-[#009E60]" />
            </div>
          </div>}
        </div>

        {isFullAdmin && (<>
        <div className="mb-8">
          <StandingsIntegrityPanel />
        </div>

        <div className="mb-8">
          <MigrationsPanel />
        </div>
        </>)}

        {/* Recent Matches */}
        {canAccess(user, "matches") && <div className="bg-white p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading font-bold text-xl uppercase text-[#09090b]">Recent Matches</h3>
            <Link to="/admin/matches" className="text-[#009E60] hover:underline text-sm font-medium">
              View All
            </Link>
          </div>
          
          {loading ? (
            <div className="space-y-4">
              {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : recentMatches.length > 0 ? (
            <div className="space-y-4">
              {recentMatches.map(match => (
                <Link to={`/matches/${match.match_id}`} key={match.match_id} className="hover:bg-gray-100 transition-colors flex items-center justify-between p-4 bg-gray-50" data-testid={`recent-match-${match.match_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <img 
                        src={teams[match.home_team_id]?.logo_url || 'https://placehold.co/32x32/09090b/FFF?text=H'} 
                        alt="" 
                        className="w-8 h-8 object-contain"
                      />
                      <span className="font-medium">{teams[match.home_team_id]?.short_name || 'HOME'}</span>
                    </div>
                    {match.status === 'completed' ? (
                      <span className="font-heading font-bold text-lg">
                        {match.home_score} - {match.away_score}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-sm">vs</span>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{teams[match.away_team_id]?.short_name || 'AWAY'}</span>
                      <img 
                        src={teams[match.away_team_id]?.logo_url || 'https://placehold.co/32x32/09090b/FFF?text=A'} 
                        alt="" 
                        className="w-8 h-8 object-contain"
                      />
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{match.match_date}</p>
                    <span className={`text-xs font-bold uppercase ${
                      match.status === 'completed' ? 'text-[#009E60]' : 
                      match.status === 'scheduled' ? 'text-blue-500' : 'text-orange-500'
                    }`}>
                      {match.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No matches yet.</p>
          )}
        </div>}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
